import axios from 'axios';
import React, { useEffect, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast.ts';

// Tree node for folder hierarchy
interface TreeNode {
  name: string;
  key: string;
  children: TreeNode[];
  hasMoreChildren: boolean;
  isLoading: boolean;
  apiPrefix: string;
  hasFetched: boolean;
}

interface S3FolderTreeSelectorProps {
  apiBaseUrl?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  showSelectedInfo?: boolean;
}

const S3FolderTreeSelector: React.FC<S3FolderTreeSelectorProps> = ({
  apiBaseUrl,
  value = '',
  onChange,
  placeholder = 'Tìm kiếm thư mục...',
  disabled = false,
  label = 'Thư mục lưu trữ trong S3',
  required = false,
  showSelectedInfo = true,
}) => {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const { toast } = useToast();

  // Fetch S3 folders for a given prefix and update a specific node in the tree
  const fetchFoldersAndUpdate = async (targetNodeKey: string, apiPrefix: string) => {
    try {
      setTreeData((prev) =>
        prev.map((node) => (node.key === targetNodeKey ? { ...node, isLoading: true } : node)),
      );

      const response = await axios.get(`${apiBaseUrl}/api/list-s3-folders`, {
        params: { prefix: apiPrefix },
      });
      const folders = response.data || {};
      const newNodes = convertToTree(folders, apiPrefix);

      setTreeData((prev) => {
        const updateNode = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.map((node) => {
            if (node.key === targetNodeKey) {
              return {
                ...node,
                children: newNodes,
                isLoading: false,
                hasMoreChildren: node.hasMoreChildren,
                hasFetched: true,
              };
            }
            if (node.children.length > 0) {
              return { ...node, children: updateNode(node.children) };
            }
            return node;
          });
        };
        return updateNode(prev);
      });
    } catch (error) {
      console.error('Error fetching S3 folders:', error);
      toast({
        title: 'Error',
        description: 'Không thể tải danh sách thư mục S3',
        variant: 'destructive',
      });
      setTreeData((prev) =>
        prev.map((node) => (node.key === targetNodeKey ? { ...node, isLoading: false } : node)),
      );
    }
  };

  // Convert flat folder map to tree structure
  const convertToTree = (
    folderMap: Record<string, any>,
    prefixForChildren: string = '',
  ): TreeNode[] => {
    const result: TreeNode[] = [];
    const seen = new Set<string>();

    Object.keys(folderMap).forEach((key) => {
      const cleanKey = key.endsWith('/') ? key.slice(0, -1) : key;
      const fullKey = cleanKey.endsWith('/') ? cleanKey : cleanKey + '/';
      let folderName: string;
      let childPrefix: string;

      if (prefixForChildren) {
        let relativeKey = fullKey;
        const prefixStripped = prefixForChildren.replace(/\/$/, '');
        if (relativeKey.startsWith(prefixStripped + '/')) {
          relativeKey = relativeKey.slice((prefixStripped + '/').length);
        } else if (relativeKey.startsWith(prefixStripped)) {
          relativeKey = relativeKey.slice(prefixStripped.length);
        }
        const relativeParts = relativeKey.split('/').filter((s) => s.length > 0);
        if (relativeParts.length === 0) return;

        folderName = relativeParts[0] || '';
        childPrefix = fullKey;
      } else {
        const parts = cleanKey.split('/').filter((s) => s.length > 0);
        if (parts.length === 0) return;
        folderName = parts[0] || '';
        childPrefix = fullKey;
      }

      if (seen.has(folderName)) return;
      seen.add(folderName);

      result.push({
        name: folderName,
        key: fullKey,
        children: [],
        hasMoreChildren: false,
        isLoading: false,
        apiPrefix: childPrefix,
        hasFetched: false,
      });
    });

    return result;
  };

  // Load root level folders on mount
  useEffect(() => {
    const loadRootFolders = async () => {
      setIsInitialLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/api/list-s3-folders`, {
          params: { prefix: '' },
        });
        const folders = response.data || {};
        setTreeData(convertToTree(folders));
      } catch (error) {
        console.error('Error loading root folders:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadRootFolders();
  }, []);

  // Handle folder expansion - load children when expanded
  const handleExpand = async (nodeKey: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      next.add(nodeKey);
      return next;
    });

    const findNode = (nodes: TreeNode[], key: string): TreeNode | undefined => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children.length > 0) {
          const found = findNode(node.children, key);
          if (found) return found;
        }
      }
      return undefined;
    };

    const node = findNode(treeData, nodeKey);
    if (node && !node.hasFetched && node.children.length === 0 && !node.isLoading) {
      await fetchFoldersAndUpdate(nodeKey, node.apiPrefix);
    }
  };

  // Handle folder collapse
  const handleCollapse = (folderKey: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      next.delete(folderKey);
      return next;
    });
  };

  // Select a folder
  const handleSelectFolder = (folderKey: string) => {
    onChange?.(folderKey);
  };

  // Check if a node should be visible based on search
  const isNodeMatchingSearch = (node: TreeNode, query: string): boolean => {
    if (!query) return true;
    const lowerQuery = query.toLowerCase();
    if (node.name.toLowerCase().includes(lowerQuery)) return true;
    return node.children.some((child) => isNodeMatchingSearch(child, query));
  };

  // Filter tree by search query
  const filterTreeBySearch = (nodes: TreeNode[], query: string): TreeNode[] => {
    if (!query) return nodes;
    return nodes
      .map((node) => ({
        ...node,
        children: filterTreeBySearch(node.children, query),
      }))
      .filter((node) => isNodeMatchingSearch(node, query));
  };

  // Expand all nodes matching search
  const expandAllMatchingNodes = (nodes: TreeNode[], query: string): Set<string> => {
    const expanded = new Set(expandedFolders);
    const processNode = (node: TreeNode) => {
      if (isNodeMatchingSearch(node, query) || node.children.length > 0) {
        expanded.add(node.key);
        node.children.forEach(processNode);
      }
    };
    nodes.forEach(processNode);
    return expanded;
  };

  // Auto-expand matching nodes when searching
  useEffect(() => {
    if (searchQuery) {
      setExpandedFolders(expandAllMatchingNodes(treeData, searchQuery));
    }
  }, [searchQuery, treeData]);

  // Render tree node
  const renderTreeNode = (node: TreeNode, depth: number = 0) => {
    const isExpanded = expandedFolders.has(node.key);
    const isSelected = value === node.key;
    const isFolder = node.key !== '';

    return (
      <div key={node.key}>
        <div
          className={`flex items-center gap-1 py-1 px-2 cursor-pointer rounded transition-colors ${
            isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => isFolder && handleSelectFolder(node.key)}
        >
          {/* Expand/Collapse toggle */}
          {isFolder && (
            <span
              className="w-4 h-4 flex items-center justify-center flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                if (isExpanded) {
                  handleCollapse(node.key);
                } else {
                  handleExpand(node.key);
                }
              }}
            >
              {/* Always show arrow by default, only show gray dot after fetch returns no children */}
              {node.hasFetched && node.children.length === 0 ? (
                <span className="w-2 h-2 rounded-full bg-gray-300"></span>
              ) : (
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  } ${node.isLoading ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </span>
          )}

          {/* Folder Icon */}
          {isFolder ? (
            <svg
              className="w-4 h-4 text-blue-500 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          ) : (
            <svg
              className="w-4 h-4 text-green-500 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.348 3.37A1 1 0 0012 8.215a1 1 0 01.948.684L14.282 12.6A1 1 0 0015.148 14H16a2 2 0 002-2V8.824a2 2 0 00-.422-1.303L13.136 3.1A2 2 0 0011.718 2H5a2 2 0 00-2 2v2z"
                clipRule="evenodd"
              />
            </svg>
          )}

          {/* Name */}
          <span className="text-sm truncate">
            {node.name || <span className="text-gray-400 italic">Root</span>}
          </span>

          {/* Selection indicator */}
          {isSelected && (
            <svg
              className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 9.586 6.707 7.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>

        {/* Children - render when expanded */}
        {isExpanded && (
          <div>
            {node.isLoading && node.children.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500" style={{ paddingLeft: '16px' }}>
                <svg className="animate-spin h-3 w-3 inline mr-1" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Đang tải...
              </div>
            ) : (
              node.children.map((child) => renderTreeNode(child, depth + 1))
            )}
          </div>
        )}
      </div>
    );
  };

  // Filtered tree data
  const filteredTree = filterTreeBySearch(treeData, searchQuery);

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center gap-1">
          <Label className="text-sm font-medium">{label}</Label>
          {required && <span className="text-red-500">*</span>}
        </div>
      )}

      {/* Search input */}
      <Input
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        disabled={disabled}
        className="text-sm"
      />

      {/* Tree container */}
      <div className="max-h-64 overflow-y-auto border rounded-md p-1 space-y-0.5">
        {isInitialLoading ? (
          <div className="flex items-center justify-center p-4 text-sm text-gray-500">
            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Đang tải thư mục...
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="flex items-center justify-center p-4 text-sm text-gray-500">
            Không có thư mục nào
          </div>
        ) : (
          <>
            {/* Root folder option (select entire S3 bucket) */}
            <div
              className={`flex items-center gap-1 py-1 px-2 cursor-pointer rounded transition-colors ${
                value === ''
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => handleSelectFolder('')}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0"></span>
              <svg
                className="w-4 h-4 text-green-500 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3 5a2 2 0 012-2h7.5a2 2 0 012 2v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm4 4h7a2 2 0 012 2v7a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm">Root (/)</span>
              {value === '' && (
                <svg
                  className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 9.586 6.707 7.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            {filteredTree.map((node) => renderTreeNode(node))}
          </>
        )}
      </div>

      {/* Selected folder info */}
      {showSelectedInfo && value && (
        <div className="space-y-1">
          <p className="text-xs text-green-600 font-medium">✓ Đã chọn: {value}</p>
          <p className="text-xs text-gray-500">Dữ liệu sẽ được lưu vào thư mục S3 này.</p>
        </div>
      )}

      {!value && showSelectedInfo && (
        <p className="text-xs text-red-500 font-medium">Vui lòng chọn thư mục để lưu dữ liệu</p>
      )}
    </div>
  );
};

export default S3FolderTreeSelector;
